/**
 * Turn a scan outcome into what the review step shows and applies. Pure, so the
 * gating ("which fields must the operator touch", "which may replace what is
 * typed") is unit-tested without a camera. Only plain text fields are applied
 * to the form; places (birth, citizenship) are shown as read names but stay for
 * the operator to resolve through the PlacePicker, and the ID document number
 * is never carried at all.
 *
 * OPS-1, two rules the dialog enforces through this file:
 *  - a field read below the confidence floor must be ticked "Verificato" before
 *    Conferma: the operator looked at it, not past it;
 *  - a field that already holds a value in the form (typed, or prefilled from
 *    the booking) keeps it unless "Sostituisci" is ticked for that row; empty
 *    fields are filled. A scan never silently overwrites what the counter typed.
 */
import { CONFIDENCE_FLOOR } from './ocr-interface'
import type { ScanOutcome } from './run-scan'

/** A form field the operator can type into directly from a scanned value. */
export type PatchField = 'surname' | 'givenNames' | 'birthDate' | 'documentType' | 'licenceNumber'

/** The plain fields a confirmed scan writes into the hand-over draft. */
export type DraftPatch = Partial<Record<PatchField, string>>

export type RowLabel = PatchField | 'birthPlaceName' | 'citizenshipName'

export interface ReviewRow {
  /** The i18n key under `m.identity` for this field's label. */
  readonly label: RowLabel
  readonly value: string
  readonly confidence: number
  readonly source: string
  /** Below the floor: highlighted, and the operator must tick it before Conferma. */
  readonly needsCheck: boolean
  /** Whether confirming the scan writes this into the form (places do not). */
  readonly applies: boolean
  /** What the form holds now for an applying field ('' when nothing, or not applying). */
  readonly current: string
}

/** What the operator ticked in the review. */
export interface ReviewDecisions {
  readonly verified: ReadonlySet<RowLabel>
  readonly replace: ReadonlySet<PatchField>
}

export const APPLYING: readonly PatchField[] = [
  'surname',
  'givenNames',
  'birthDate',
  'documentType',
  'licenceNumber',
]

const isPatchField = (label: RowLabel): label is PatchField =>
  (APPLYING as readonly string[]).includes(label)

/** The rows to show in review, in reading order; low-confidence ones flagged. */
export function reviewRows(outcome: ScanOutcome, current: DraftPatch = {}): ReviewRow[] {
  const p = outcome.prefill
  const rows: ReviewRow[] = []
  const push = (
    label: RowLabel,
    f: { value: string; confidence: number; source: string } | undefined,
    applies: boolean,
  ) => {
    if (!f?.value) return
    rows.push({
      label,
      value: f.value,
      confidence: f.confidence,
      source: f.source,
      needsCheck: f.confidence < CONFIDENCE_FLOOR,
      applies,
      current: applies && isPatchField(label) ? (current[label] ?? '').trim() : '',
    })
  }
  push('surname', p.surname, true)
  push('givenNames', p.givenNames, true)
  push('birthDate', p.birthDate, true)
  push('birthPlaceName', p.birthPlaceName, false)
  push('citizenshipName', p.citizenshipName, false)
  push('documentType', p.documentType, true)
  push('licenceNumber', p.licenceNumber, true)
  return rows
}

/** The rows the operator must still tick before Conferma. */
export function unverified(rows: readonly ReviewRow[], decisions: ReviewDecisions): ReviewRow[] {
  return rows.filter((row) => row.needsCheck && !decisions.verified.has(row.label))
}

/**
 * The plain fields a confirmed scan applies to the draft (no places, no ID
 * number): an empty field takes the read value; a field with a value keeps it
 * unless the operator ticked "Sostituisci" for it.
 */
export function draftPatch(
  outcome: ScanOutcome,
  current: DraftPatch,
  decisions: ReviewDecisions,
): DraftPatch {
  const patch: DraftPatch = {}
  for (const row of reviewRows(outcome, current)) {
    if (!row.applies || !isPatchField(row.label)) continue
    if (row.current === '' || decisions.replace.has(row.label)) patch[row.label] = row.value
  }
  return patch
}

/** True when the scan read nothing it could write (places alone fill no field). */
export function scanEmpty(outcome: ScanOutcome): boolean {
  return !reviewRows(outcome).some((row) => row.applies)
}

/** The form fields a scan could fill but did not: said as "partial", typed by hand. */
export function unreadFields(outcome: ScanOutcome): PatchField[] {
  const read = new Set(reviewRows(outcome).map((row) => row.label))
  return APPLYING.filter((field) => !read.has(field))
}
